import type { ParsedCartItemGroup } from 'ui/types/cartTypes';
import type { CountryCode } from '@whoop/i18n';
import clsx from 'clsx';
import { Separator } from 'ui/components/Separator/Separator';
import { TRIAL_MEMBERSHIP_SKU } from 'ui/utils/constants/membershipConstants';
import { FAMILY_MEMBERSHIP_SKUS } from 'ui/utils/constants/familyConstants';
import { PlanTypes } from 'ui/utils/constants/maverick';
import { usePathname } from 'next/navigation';
import { isExtensionInCartItems } from '../cart.utils';
import CartItem from '@/components/Cart/CartItems/CartItem/CartItem';
import { HsaFsaBadge } from '@/components/HsaFsaBadge/HsaFsaBadge';
import {
  getIsMembershipTypeInCart,
  getIsSomeMembershipInCart,
} from '@/lib/utils/membershipHelper';
import { TruemedHsaFsaBadge } from '@/components/TruemedHsaFsaBadge/TruemedHsaFsaBadge';
import { useShouldShowTruemedBanner } from '@/stripe/customPaymentMethods/truemed/client/useShouldShowTruemedBanner';

interface CartGroupsProps {
  cartId: string | null;
  cartItemGroups?: ParsedCartItemGroup[];
  country: CountryCode;
  isCartLocked?: boolean;
  hideSeparators?: boolean;
  hideHsaFsaBadge?: boolean;
}

export default function CartGroups({
  cartId,
  cartItemGroups,
  country,
  isCartLocked = false,
  hideSeparators = false,
  hideHsaFsaBadge = false,
}: CartGroupsProps): JSX.Element | null {
  const pathname = usePathname();
  const shouldShowTruemedBanner = useShouldShowTruemedBanner();

  if (cartId === null || !cartItemGroups) {
    return null;
  }

  const isTrialInCart =
    getIsSomeMembershipInCart(cartItemGroups, [TRIAL_MEMBERSHIP_SKU]) ||
    // Get by attributes
    getIsMembershipTypeInCart(cartItemGroups, PlanTypes.TRIAL);

  const is24MonthMembershipInCart = getIsMembershipTypeInCart(
    cartItemGroups,
    PlanTypes.TWO_YEAR,
  );

  const isFamilyInCart =
    getIsSomeMembershipInCart(cartItemGroups, FAMILY_MEMBERSHIP_SKUS) ||
    // Get by attributes
    getIsMembershipTypeInCart(cartItemGroups, PlanTypes.FAMILY);

  const showHSABadge = () => {
    const shouldShow =
      !isFamilyInCart && !is24MonthMembershipInCart && !hideHsaFsaBadge;

    if (pathname.includes('/upgrade')) {
      return shouldShow && isExtensionInCartItems(cartItemGroups);
    }
    return shouldShow;
  };

  return (
    <div className='grid w-full grid-cols-1 gap-4 md:gap-6'>
      {cartItemGroups?.map((cartItemGroup, index) => (
        <div
          className={clsx(
            'rounded-xl bg-white',
            !hideSeparators && 'px-4 pb-1 pt-4',
            (index !== 0 || (index === 0 && !showHSABadge())) &&
              !hideSeparators &&
              'pb-3',
            cartItemGroup.cartItems.length > 0 ? 'visible' : 'hidden',
          )}
          key={cartItemGroup.title}
        >
          <div className={clsx('space-y-4')}>
            <div className='grid grid-cols-1'>
              <h3 className='text-xl font-bold'>{cartItemGroup.title}</h3>
              <h3 className='text-gray-60 font-regular text-sm'>
                {cartItemGroup.description}
              </h3>
            </div>
            {!hideSeparators ? <Separator /> : null}
            {cartItemGroup.cartItems.map((item) => (
              <div className='w-full space-y-3' key={item.id}>
                <CartItem
                  cartId={cartId}
                  cartItem={item}
                  isCartLocked={isCartLocked}
                  isTrialInCart={isTrialInCart}
                />
              </div>
            ))}
          </div>
          {index === 0 && showHSABadge() ? (
            <div className='text-gray-b50 flex w-full justify-start pb-2'>
              {shouldShowTruemedBanner && !isTrialInCart ? (
                <TruemedHsaFsaBadge />
              ) : (
                <HsaFsaBadge
                  country={country}
                  isTrialInCart={isTrialInCart}
                  size='small'
                />
              )}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
